import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ListingCard } from "@/components/ListingCard";
import { FitmentFilters } from "@/components/FitmentFilters";
import { VehiclePicker } from "@/components/VehiclePicker";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { SlidersHorizontal, Navigation, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FILTER_KEYS = ["q", "category", "bolt_pattern", "diameter", "width", "offset", "tire_size", "condition", "fulfillment", "location", "min_price", "max_price"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [constants, setConstants] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("boosted");
  const [near, setNear] = useState(null); // {lat, lng, label}
  const [zipInput, setZipInput] = useState("");
  const [locating, setLocating] = useState(false);
  const [filters, setFilters] = useState(() => {
    const init = {};
    FILTER_KEYS.forEach((k) => { const v = searchParams.get(k); if (v) init[k] = v; });
    return init;
  });

  useEffect(() => { api.get("/constants").then((r) => setConstants(r.data)); }, []);

  const fetchListings = useCallback(async (f, s, nearObj) => {
    setLoading(true);
    const params = { sort: s };
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    if (nearObj) { params.near_lat = nearObj.lat; params.near_lng = nearObj.lng; }
    try {
      const r = await api.get("/listings", { params });
      setListings(r.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchListings(filters, sort, near); /* eslint-disable-next-line */ }, [sort]);
  // initial load
  useEffect(() => { fetchListings(filters, sort, near); /* eslint-disable-next-line */ }, []);

  const locateMe = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const n = { lat: +pos.coords.latitude.toFixed(4), lng: +pos.coords.longitude.toFixed(4), label: "Your location" };
        setNear(n); setLocating(false); setSort("distance");
        fetchListings(filters, "distance", n);
      },
      () => { setLocating(false); toast.error("Couldn't get your location — try a ZIP instead"); },
      { timeout: 8000 }
    );
  };

  const searchByZip = async () => {
    if (!zipInput.trim()) return;
    setLocating(true);
    try {
      const r = await api.get(`/geocode`, { params: { zip: zipInput.trim() } });
      const n = { lat: r.data.lat, lng: r.data.lng, label: r.data.place ? `${r.data.place}, ${r.data.state}` : zipInput };
      setNear(n); setSort("distance");
      fetchListings(filters, "distance", n);
    } catch {
      toast.error("ZIP not found");
    } finally {
      setLocating(false);
    }
  };

  const clearNear = () => { setNear(null); setZipInput(""); setSort("boosted"); fetchListings(filters, "boosted", null); };

  const apply = () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    setSearchParams(p);
    fetchListings(filters, sort, near);
  };

  const clear = () => {
    setFilters({});
    setSearchParams(new URLSearchParams());
    fetchListings({}, sort, near);
  };

  const onVehicle = (v) => {
    const next = { ...filters, bolt_pattern: v.bolt_pattern };
    setFilters(next);
    const p = new URLSearchParams();
    Object.entries(next).forEach(([k, val]) => { if (val) p.set(k, val); });
    setSearchParams(p);
    fetchListings(next, sort, near);
  };

  const saveSearch = async () => {
    if (!user) { toast.error("Sign in to save searches"); return; }
    const active = Object.entries(filters).filter(([, v]) => v);
    if (active.length === 0) { toast.error("Add at least one filter"); return; }
    const labels = { category: "", bolt_pattern: "", diameter: '" dia', width: '" wide', offset: " ET", tire_size: "", condition: "", fulfillment: "", location: "", q: "" };
    const name = active.map(([k, v]) => `${v}${labels[k] ?? ""}`).slice(0, 4).join(" · ");
    try {
      await api.post("/saved-searches", { name, filters });
      toast.success("Search saved — check your dashboard for matches");
    } catch {
      toast.error("Could not save search");
    }
  };

  return (
    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar (desktop) */}
        <aside className="hidden lg:block w-80 shrink-0">
          <div className="sticky top-20 bg-[#141414] border border-border rounded-sm p-5 space-y-5">
            <div className="pb-5 border-b border-border">
              <VehiclePicker onResolve={onVehicle} />
            </div>
            <FitmentFilters constants={constants} filters={filters} setFilters={setFilters} onApply={apply} onClear={clear} onSave={saveSearch} />
          </div>
        </aside>

        {/* Results */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-4 gap-3">
            <div>
              <h1 className="font-heading font-800 text-xl uppercase tracking-tight">Marketplace</h1>
              <p className="text-xs text-muted-foreground mt-0.5" data-testid="results-count">{loading ? "Searching…" : `${listings.length} listing${listings.length === 1 ? "" : "s"} found`}</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Mobile filter drawer */}
              <div className="lg:hidden">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button data-testid="mobile-filters-btn" variant="outline" className="border-border bg-[#141414] rounded-sm gap-2">
                      <SlidersHorizontal className="w-4 h-4" /> Filters
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="bg-[#141414] border-border w-[85vw] max-w-sm overflow-y-auto">
                    <div className="mt-6 space-y-5">
                      <div className="pb-5 border-b border-border">
                        <VehiclePicker onResolve={onVehicle} />
                      </div>
                      <FitmentFilters constants={constants} filters={filters} setFilters={setFilters} onApply={apply} onClear={clear} onSave={saveSearch} />
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="sort-select" className="w-40 bg-[#141414] border-border rounded-sm h-10 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-border">
                  <SelectItem value="boosted">Boosted first</SelectItem>
                  <SelectItem value="newest">Newest</SelectItem>
                  {near && <SelectItem value="distance">Nearest</SelectItem>}
                  <SelectItem value="price_asc">Price: Low to High</SelectItem>
                  <SelectItem value="price_desc">Price: High to Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Near me bar */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {near ? (
              <div data-testid="near-active" className="flex items-center gap-2 bg-blaze/10 border border-blaze/40 rounded-sm px-3 h-9 text-sm">
                <Navigation className="w-4 h-4 text-blaze" />
                <span className="text-blaze font-bold">Near {near.label}</span>
                <button data-testid="clear-near-btn" onClick={clearNear} className="text-muted-foreground hover:text-white"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <>
                <Button data-testid="near-me-btn" onClick={locateMe} disabled={locating} variant="outline" className="border-border bg-[#141414] rounded-sm h-9 gap-2 text-sm hover:border-blaze/50">
                  {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4 text-blaze" />} Near me
                </Button>
                <div className="flex items-center gap-1.5">
                  <Input data-testid="near-zip-input" value={zipInput} onChange={(e) => setZipInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchByZip()} placeholder="or ZIP" className="w-24 bg-[#141414] border-border rounded-sm h-9 text-sm spec-block" />
                  <Button data-testid="near-zip-btn" onClick={searchByZip} disabled={locating} variant="outline" className="border-border bg-[#141414] rounded-sm h-9 text-sm">Go</Button>
                </div>
              </>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-80 bg-[#141414] border border-border rounded-sm animate-pulse" />)}
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-24 border border-dashed border-border rounded-sm">
              <p className="font-heading font-800 text-lg uppercase">No matches</p>
              <p className="text-sm text-muted-foreground mt-1">Try loosening a filter or two.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
